import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap, finalize } from 'rxjs/operators';
import { 
  SearchItem, 
  SearchResponse, 
  PathResponse, 
  ChildrenResponse, 
  NavNode,
  SearchParams,
  ChildrenParams 
 } from './../../modules/navigation/models/nav.models'
import { ApiRequestService } from '@common/services/api-request.service';
import { ToastService } from '@common/services';

@Injectable({
  providedIn: 'root'
})
export class NavService {

  private readonly baseUrl = ''
  private readonly statsStoragePrefix = 'nav:stats';
  private statsLoadInFlight = false;
  private statsRetryHandle: number | null = null;
  private statsLoadedForUserId: string | null = null;

  private getStatsStorageKey(): string {
    const userId = localStorage.getItem('userID') || 'anonymous';
    return `${this.statsStoragePrefix}:${userId}`;
  }

  private readCachedStats(): {
    meteringFarms: number;
    meteringSections: number;
    meteringPoints: number;
    meters: number;
  } | null {
    try {
      const raw = localStorage.getItem(this.getStatsStorageKey());
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return {
        meteringFarms: Number(parsed?.meteringFarms ?? 0) || 0,
        meteringSections: Number(parsed?.meteringSections ?? 0) || 0,
        meteringPoints: Number(parsed?.meteringPoints ?? 0) || 0,
        meters: Number(parsed?.meters ?? 0) || 0,
      };
    } catch {
      return null;
    }
  }

  private cacheStats(stats: {
    meteringFarms: number;
    meteringSections: number;
    meteringPoints: number;
    meters: number;
  }): void {
    try {
      localStorage.setItem(this.getStatsStorageKey(), JSON.stringify(stats));
    } catch {
      // Ignore storage failures and continue with in-memory state.
    }
  }
  
  // State management
  private searchResultsSubject = new BehaviorSubject<SearchItem[]>([]);
  private selectedMeterSubject = new BehaviorSubject<string | null>(null);
  private treeStateSubject = new BehaviorSubject<Map<string, NavNode>>(new Map());
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private statsSubject = new BehaviorSubject<{
    meteringFarms: number;
    meteringSections: number;
    meteringPoints: number;
    meters: number;
  } | null>(this.readCachedStats());
  private searchMetaSubject = new BehaviorSubject<{ total: number; hasMore: boolean; cursor?: string } | null>(null);
  private rootMetaSubject = new BehaviorSubject<{ total: number; hasMore: boolean; cursor?: string } | null>(null);
  private isSearchModeSubject = new BehaviorSubject<boolean>(false);

  // Public observables
  public searchResults$ = this.searchResultsSubject.asObservable();
  public selectedMeter$ = this.selectedMeterSubject.asObservable();
  public treeState$ = this.treeStateSubject.asObservable();
  public treeNodes$ = this.treeStateSubject.asObservable().pipe(
    map(state => Array.from(state.values()))
  );
  public loading$ = this.loadingSubject.asObservable();
  public error$ = this.errorSubject.asObservable();
  public stats$ = this.statsSubject.asObservable();
  public searchMeta$ = this.searchMetaSubject.asObservable();
  public rootMeta$ = this.rootMetaSubject.asObservable();
  public isSearchMode$ = this.isSearchModeSubject.asObservable();

  constructor(private http: HttpClient , private apiRequest: ApiRequestService ,  private toastService: ToastService,) {}

  getStatsSnapshot(): {
    meteringFarms: number;
    meteringSections: number;
    meteringPoints: number;
    meters: number;
  } | null {
    return this.statsSubject.value;
  }

  setStats(
    stats: {
      meteringFarms: number;
      meteringSections: number;
      meteringPoints: number;
      meters: number;
    },
    userIdRaw?: string | number | null
  ): void {
    this.publishStats(stats, userIdRaw);
  }

  private isReportingRoute(): boolean {
    try {
      return window.location.pathname.includes('/dashboard/reporting');
    } catch {
      return false;
    }
  }

  /**
   * Reset root-level metadata (e.g., when starting a new search)
   */
  resetRootMeta(): void {
    this.rootMetaSubject.next(null);
  }

  /**
   * Toggle search mode; when active we suppress root loading/metadata.
   */
  setSearchMode(active: boolean): void {
    this.isSearchModeSubject.next(active);
    if (active) {
      // Clear root metadata while searching so UI doesn't show root paging
      this.resetRootMeta();
    }
  }

  loadStats(): void {
    if (this.statsLoadInFlight) {
      return;
    }

    const userIdRaw = localStorage.getItem('userID');
    const userId = userIdRaw ? Number(userIdRaw) : NaN;
    if (userIdRaw && this.statsLoadedForUserId === String(userIdRaw) && this.statsSubject.value) {
      return;
    }
    if (!Number.isFinite(userId) || userId <= 0) {
      const cachedStats = this.readCachedStats();
      if (cachedStats) {
        this.statsSubject.next(cachedStats);
      }
      this.scheduleStatsRetry();
      return;
    }

    this.statsLoadInFlight = true;
    const body: any = {
      payload: {
        userID: userId,
      },
    };

    this.apiRequest
        .apiRequest(
          'metering/navigation/stats',
          'stats.json',
          body
        ).subscribe({
          next: (res) => {
          console.log(res , 'stats')
          const stats = {
            meteringFarms: Number(res?.data?.meteringFarms ?? 0) || 0,
            meteringSections: Number(res?.data?.meteringSections ?? 0) || 0,
            meteringPoints: Number(res?.data?.meteringPoints ?? 0) || 0,
            meters: Number(res?.data?.meters ?? 0) || 0,
          };
          this.publishStats(stats, userIdRaw);
          this.statsLoadInFlight = false;
          this.clearStatsRetry();
          },
          error: (error) => {
            console.warn('[NavService] Failed to load navigation stats', error);
            const fallbackStats = this.readCachedStats() ?? {
              meteringFarms: 0,
              meteringSections: 0,
              meteringPoints: 0,
              meters: 0,
            };
            this.publishStats(fallbackStats, userIdRaw);
            this.statsLoadInFlight = false;
            this.clearStatsRetry();
          }
        })
  }

  private publishStats(
    stats: {
      meteringFarms: number;
      meteringSections: number;
      meteringPoints: number;
      meters: number;
    },
    userIdRaw?: string | number | null
  ): void {
    this.cacheStats(stats);
    this.statsSubject.next(stats);
    if (userIdRaw !== null && userIdRaw !== undefined && String(userIdRaw).trim()) {
      this.statsLoadedForUserId = String(userIdRaw);
    }
  }

  private scheduleStatsRetry(): void {
    if (this.statsRetryHandle !== null) {
      return;
    }

    this.statsRetryHandle = window.setTimeout(() => {
      this.statsRetryHandle = null;
      this.loadStats();
    }, 250);
  }

  private clearStatsRetry(): void {
    if (this.statsRetryHandle !== null) {
      window.clearTimeout(this.statsRetryHandle);
      this.statsRetryHandle = null;
    }
  }

  getTotalCount(): Observable<{total: number, description: string, breakdown: any}> {
    return this.http.get<{total: number, description: string, breakdown: any}>(`${this.baseUrl}/total`).pipe(
      catchError(error => {
        console.error('Error fetching navigation total count:', error);
        // Return fallback count on error
        return of({total: 0, description: 'Unable to fetch total count', breakdown: {}});
      })
    );
  }

  /**
   * Lookup farms by name prefix with pagination
   */
  lookupFarms(query: string, limit: number = 25, cursor?: string) {
    let params = new HttpParams().set('query', query || '').set('limit', String(limit));
    if (cursor) params = params.set('cursor', cursor);
    return this.http.get<{ items: { id: string; name: string }[]; total: number; hasMore: boolean; cursor?: string }>(
      `${this.baseUrl}/farms`,
      { params }
    );
  }

  /**
   * Lookup sections by farm and name prefix with pagination
   */
  lookupSections(farmId: string, query: string, limit: number = 25, cursor?: string) {
    let params = new HttpParams().set('farmId', farmId).set('query', query || '').set('limit', String(limit));
    if (cursor) params = params.set('cursor', cursor);
    return this.http.get<{ items: { id: string; name: string }[]; total: number; hasMore: boolean; cursor?: string }>(
      `${this.baseUrl}/sections`,
      { params }
    );
  }

  /**
   * Search for meters with debounced input
   */
  searchMeters(params: SearchParams): Observable<any> {
    // Guard: ignore queries shorter than 3 characters
    const trimmed = (params?.query || '').trim();
    if (trimmed.length < 3) {
      return of({ data: { data: [], pagination: { total: 0, next: null, page: 1 } } });
    }
    this.setSearchMode(true);
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    let body: any = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
        searchTerm:params.query,
        page:params.cursor || 1,
        perPage: params.limit || 20
      },
    };
    
    if (params.cursor) {
      return this.apiRequest
        .apiRequest(
          'metering/navigation/search/',
          'search.json',
          body,
          { cancelable: this.isReportingRoute() ? false : undefined }
        )
        .pipe(
          map(response => this.normalizePaginatedResponse(response, Number(params.cursor) || 1)),
          tap(response => {
            const payload = response.data;
            const hasMore = Boolean(payload.pagination.next);
            this.searchResultsSubject.next(payload.data);
            this.searchMetaSubject.next({ total: payload.pagination.total, hasMore, cursor: payload.pagination.page });
          }),
          catchError(error => {
            this.handleError('Search failed', error);
            return of(this.emptyPaginatedResponse());
          }),
          finalize(() => this.loadingSubject.next(false))
        );
    }

    return this.apiRequest
      .apiRequest(
        'metering/navigation/search/',
        'children.json',
        body,
        { cancelable: this.isReportingRoute() ? false : undefined }
      )
      .pipe(
        map(response => this.normalizePaginatedResponse(response, 1)),
        tap(response => {
          const payload = response.data;
          const hasMore = Boolean(payload.pagination.next);
          this.searchResultsSubject.next(payload.data);
          this.searchMetaSubject.next({ total: payload.pagination.total, hasMore, cursor: payload.pagination.page });
          }),
        catchError(error => {
          this.handleError('Search failed', error);
          return of(this.emptyPaginatedResponse());
        }),
        finalize(() => this.loadingSubject.next(false))
      );
  }

  private emptyPaginatedResponse(page = 1): any {
    return { data: { data: [], pagination: { total: 0, next: null, page } } };
  }

  private normalizePaginatedResponse(response: any, page = 1): any {
    const payload = response?.data ?? {};
    const rows = Array.isArray(payload.data) ? payload.data : [];
    const pagination = payload.pagination ?? {};
    return {
      ...response,
      data: {
        ...payload,
        data: rows,
        pagination: {
          total: Number(pagination.total ?? rows.length) || 0,
          next: pagination.next ?? null,
          page: pagination.page ?? page
        }
      }
    };
  }

  loadMoreSearch(params: any): Observable<any> {
    if (!params.query || params.query.trim().length < 3) return of({ data: { data: [], pagination: { total: 0, next: null, page: 1 } } });
    const meta = this.searchMetaSubject.value;
    console.log(params.query , 'query')
    console.log(meta , 'meta ')
    if (!meta?.hasMore) return of({ data: { data: [], pagination: { total: 0, next: null, page: 1 } } });
   
    this.loadingSubject.next(true);

    // Ensure numeric cursor increment regardless of stored type
    let latestCursor: number = Number(meta.cursor ?? 1) + 1

    let body: any = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
        searchTerm:params.query,
        page:latestCursor,
        perPage: params.limit || 20
      },
    };

     console.log(body , 'body ')

     return this.apiRequest
      .apiRequest(
        'metering/navigation/search/',
        'search.json',
        body,
        { cancelable: this.isReportingRoute() ? false : undefined }
      )
      .pipe(
        map(response => this.normalizePaginatedResponse(response, latestCursor)),
        tap(res => {
          const payload = res.data;
          const hasMore = Boolean(payload.pagination.next);
          const current = this.searchResultsSubject.value;
          this.searchResultsSubject.next([...current, ...payload.data]);
          this.searchMetaSubject.next({ total: payload.pagination.total, hasMore, cursor: payload.pagination.page });
        }),
        catchError(error => {
          this.handleError('Search failed', error);
          return of(this.emptyPaginatedResponse());
        }),
        finalize(() => this.loadingSubject.next(false))
      );
   

    // return this.http
    //   .get<SearchResponse>(`${this.baseUrl}/search`, { params: httpParams })
    //   .pipe(catchError(() => of({ items: [], total: meta.total, hasMore: false } as any)))
    //   .subscribe(res => {
    //     const current = this.searchResultsSubject.value;
    //     this.searchResultsSubject.next([...current, ...res.items]);
    //     this.searchMetaSubject.next({ total: res.total, hasMore: res.hasMore, cursor: res.cursor });
    //     this.loadingSubject.next(false);
    //   });
  }

  /**
   * Get the full path to a meter and expand the tree
   */
  getMeterPath(meterId: string): Observable<PathResponse> {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return this.http.get<PathResponse>(`${this.baseUrl}/path`, {
      params: { meterId }
    }).pipe(
      tap(response => {
        this.expandPath(response.path);
        this.selectedMeterSubject.next(meterId);
        this.loadingSubject.next(false);
      }),
      catchError(error => {
        this.handleError('Failed to get meter path', error);
        throw error;
      })
    );
  }

  /**
   * Get children of a specific parent node
   */
  getChildren(params: ChildrenParams): Observable<any> {
    console.log(params , 'params')
    // If searching and caller is asking for root-level children, suppress.
    if (this.isSearchModeSubject.value && !params.parentType && !params.parentID) {
      return of({ data: { data: [], pagination: { total: 0, next: null, page: 1 } } });
    }
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    const httpParams = new HttpParams()
      .set('parentType', params.parentType)
      .set('parentID', params.parentID)
      .set('limit', params.limit?.toString() || '50');

    let body: any = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
        perPage: params.limit || 20,
        page:params.cursor || 1
      },
    };
    
    if(params.parentType){
      body.payload.parentType = params.parentType
    }

     if(params.parentID){
      body.payload.parentID = params.parentID
    }

    if (params.parentType === 'point') {
      body.payload.isActive = 'y';
    }
    
    if (params.cursor) {
      const withCursor = httpParams.set('cursor', params.cursor);
      return this.apiRequest
        .apiRequest('metering/navigation/children/', 'children.json', body, { cancelable: false })
        .pipe(
          map(response => this.normalizePaginatedResponse(response, params.cursor || 1)),
          tap(response => {
            console.log(`[NavService] Children response for ${params.parentType}:${params.parentID}:`, response);
          }),
          catchError(error => {
            console.error(`[NavService] Failed to get children for ${params.parentType}:${params.parentID}:`, error);
            // Provide detailed error information
            let errorMessage = 'Failed to get children';
            if (error.status === 0) {
              errorMessage = 'Network error - CORS or connection issue. Check if backend is running on localhost:8000';
            } else if (error.status === 500) {
              errorMessage = 'Backend server error - check Laravel logs';
            } else if (error.status === 400) {
              errorMessage = `Bad request: ${error.error?.error || 'Invalid parameters'}`;
            } else if (error.status === 404) {
              errorMessage = 'Endpoint not found';
            }
            this.errorSubject.next(errorMessage);
            return of({ data: { data: [], pagination: { total: 0, next: null, page: params.cursor || 1 } } });
          }),
          finalize(() => this.loadingSubject.next(false))
        );
    }

    return this.apiRequest
      .apiRequest('metering/navigation/children/', 'children.json', body, { cancelable: false })
      .pipe(
        map(response => this.normalizePaginatedResponse(response, params.cursor || 1)),
        tap(response => {
          console.log(`[NavService] Children response for ${params.parentType}:${params.parentID}:`, response);
        }),
        catchError(error => {
          console.error(`[NavService] Failed to get children for ${params.parentType}:${params.parentID}:`, error);
          // Provide detailed error information
          let errorMessage = 'Failed to get children';
          if (error.status === 0) {
            errorMessage = 'Network error - CORS or connection issue. Check if backend is running on localhost:8000';
          } else if (error.status === 500) {
            errorMessage = 'Backend server error - check Laravel logs';
          } else if (error.status === 400) {
            errorMessage = `Bad request: ${error.error?.error || 'Invalid parameters'}`;
          } else if (error.status === 404) {
            errorMessage = 'Endpoint not found';
          }
          this.errorSubject.next(errorMessage);
          return of({ data: { data: [], pagination: { total: 0, next: null, page: params.cursor || 1 } } });
        }),
        finalize(() => this.loadingSubject.next(false))
      );

  }

  /**
   * Expand a node in the tree
   */
  expandNode(nodeId: string, nodeType: 'farm' | 'section' | 'point'): void {
    const currentState = this.treeStateSubject.value;
    const node = currentState.get(nodeId);
    
    if (!node || node.expanded) {
      return;
    }

    // Accordion behavior: collapse sibling nodes at the same level (same parent)
    try {
      const parentId = node.parentID;
      for (const sibling of currentState.values()) {
        if (sibling && sibling.id !== nodeId && sibling.parentID === parentId && sibling.type === nodeType) {
          sibling.expanded = false;
        }
      }
    } catch {}

    // If node already has children, just expand without fetching
    if (node.children && node.children.length > 0) {
      node.expanded = true;
      this.treeStateSubject.next(new Map(currentState));
      return;
    }

    // Mark as loading
    node.loading = true;
    node.expanded = true;
    this.treeStateSubject.next(new Map(currentState));

    // Load children
    this.getChildren({
      parentType: nodeType,
      parentID: nodeId,
      limit: 20
    }).subscribe({
      next: response => {

     
      const hasMore = (response.data.pagination.next) ? true : false
      const updatedState = this.treeStateSubject.value;
      const updatedNode = updatedState.get(nodeId);
      
      if (updatedNode) {
        // Merge children into map for further expansions
        response.data.data.forEach((child:any) => {
          if (!updatedState.has(child.id)) {
            updatedState.set(child.id, { ...child, expanded: false, loading: false, children: [] });
          }
        });
        updatedNode.children = response.data.data.map((c:any) => updatedState.get(c.id)!);
        updatedNode.expanded = true;
        updatedNode.hasMore = hasMore;
        updatedNode.cursor = response.data.pagination.page;
        updatedNode.loading = false;

        console.log(updatedState , ' updated state')
        
        this.treeStateSubject.next(new Map(updatedState));
      }
    },
    error: () => {
      const updatedState = this.treeStateSubject.value;
      const updatedNode = updatedState.get(nodeId);
      if (updatedNode) {
        updatedNode.loading = false;
        this.treeStateSubject.next(new Map(updatedState));
      }
    },
    complete: () => {
      const updatedState = this.treeStateSubject.value;
      const updatedNode = updatedState.get(nodeId);
      if (updatedNode && updatedNode.loading) {
        updatedNode.loading = false;
        this.treeStateSubject.next(new Map(updatedState));
      }
    }
    });
  }

  /**
   * Load more children for pagination
   */
  loadMoreChildren(nodeId: string, nodeType: 'farm' | 'section' | 'point'): void {

    console.log(nodeId , nodeType)



    const currentState = this.treeStateSubject.value;
    const node = currentState.get(nodeId);

    
   
    
    if (!node || !node.hasMore || node.loading) return;

    node.loading = true;
    this.treeStateSubject.next(new Map(currentState));
    let curr = node.cursor + 1
   console.log(curr , 'node node   ')
    this.getChildren({
      parentType: nodeType,
      parentID: nodeId,
      limit: 20,
      cursor: curr
    }).subscribe({
      next: response => {
       const hasMore = (response.data.pagination.next) ? true : false
      const updatedState = this.treeStateSubject.value;
      const updatedNode = updatedState.get(nodeId);
      
      if (updatedNode && updatedNode.children) {
        // Merge new children into state map first
        response.data.data.forEach((child:any) => {
          if (!updatedState.has(child.id)) {
            updatedState.set(child.id, { ...child, expanded: false, loading: false, children: [] });
          }
        });
        updatedNode.children = [...updatedNode.children, ...response.data.data.map((c:any) => updatedState.get(c.id)!)];
        updatedNode.hasMore = hasMore;
        updatedNode.cursor = response.data.pagination.page;
        updatedNode.loading = false;
        
        this.treeStateSubject.next(new Map(updatedState));
      }
    },
    error: () => {
      const updatedState = this.treeStateSubject.value;
      const updatedNode = updatedState.get(nodeId);
      if (updatedNode) {
        updatedNode.loading = false;
        this.treeStateSubject.next(new Map(updatedState));
      }
    },
    complete: () => {
      const updatedState = this.treeStateSubject.value;
      const updatedNode = updatedState.get(nodeId);
      if (updatedNode && updatedNode.loading) {
        updatedNode.loading = false;
        this.treeStateSubject.next(new Map(updatedState));
      }
    }
    });
  }

  /**
   * Force refresh a node's children from page 1, replacing existing children.
   */
  refreshNodeChildren(nodeId: string, nodeType: 'farm' | 'section' | 'point'): void {
    const currentState = this.treeStateSubject.value;
    const node = currentState.get(nodeId);
    if (!node) return;

    node.loading = true;
    node.expanded = true;
    this.treeStateSubject.next(new Map(currentState));

    this.getChildren({ parentType: nodeType, parentID: nodeId, limit: 20, cursor: 1 }).subscribe({
      next: (response: any) => {
        const hasMore = (response?.data?.pagination?.next) ? true : false;
        const updatedState = this.treeStateSubject.value;
        const updatedNode = updatedState.get(nodeId);
        if (updatedNode) {
          // Ensure children exist in map and update basic props
          const rawChildren = response?.data?.data || [];
          rawChildren.forEach((child: any) => {
            if (!updatedState.has(child.id)) {
              updatedState.set(child.id, { ...child, expanded: false, loading: false, children: [] });
            } else {
              const existing = updatedState.get(child.id)!;
              updatedState.set(child.id, { ...existing, ...child });
            }
          });
          updatedNode.children = rawChildren.map((c: any) => updatedState.get(c.id)!);
          updatedNode.hasMore = hasMore;
          updatedNode.cursor = response?.data?.pagination?.page ?? 1;
          updatedNode.loading = false;
          this.treeStateSubject.next(new Map(updatedState));
        }
      },
      error: () => {
        const updatedState = this.treeStateSubject.value;
        const updatedNode = updatedState.get(nodeId);
        if (updatedNode) {
          updatedNode.loading = false;
          this.treeStateSubject.next(new Map(updatedState));
        }
      }
    });
  }

  /**
   * Collapse a node in the tree
   */
  collapseNode(nodeId: string): void {
    const currentState = this.treeStateSubject.value;
    const node = currentState.get(nodeId);
    
    if (node) {
      node.expanded = false;
      this.treeStateSubject.next(new Map(currentState));
    }
  }

  /**
   * Expand all nodes in the tree
   */
  expandAll(): void {
    const currentState = this.treeStateSubject.value;
    const newState = new Map(currentState);
    
    for (const node of newState.values()) {
      if (node.type !== 'meter') {
        node.expanded = true;
        if (!node.children || node.children.length === 0) {
          if (node.type === 'farm' || node.type === 'section' || node.type === 'point') {
            this.expandNode(node.id, node.type);
          }
        }
      }
    }
    
    this.treeStateSubject.next(newState);
  }

  /**
   * Select a meter and update the URL
   */
  selectMeter(meterId: string): void {
    this.selectedMeterSubject.next(meterId);
    
    // Update URL for deep linking
    const url = new URL(window.location.href);
    url.searchParams.set('meterId', meterId);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Clear search results
   */
  clearSearch(): void {
    this.searchResultsSubject.next([]);
    this.errorSubject.next(null);
    this.searchMetaSubject.next(null);
    // Reset root view
    this.treeStateSubject.next(new Map());
    // Only load root farms when not in search mode
    if (!this.isSearchModeSubject.value) {
      this.loadRootFarms();
    }
  }

  /**
   * Load root farms and merge into existing state (non-destructive)
   */
  loadRootFarms(): void {
    if (this.isSearchModeSubject.value) {
      // Suppress root loading during active search mode
      return;
    }
    console.log('[NavService] loadRootFarms()');
    this.loadingSubject.next(true);
    this.getChildren({ parentType: '', parentID: '', limit: 20 }).subscribe({
      next: response => {
        console.log('[NavService] root children received:', response);
        const farms = response.data.data.map((farm:any) => ({
          ...farm,
          parentID: 'root',
          expanded: false,
          loading: false,
          children: [],
          hasMore: false,
          cursor: undefined
        }));
        const current = this.treeStateSubject.value;
        const merged = new Map(current);
        farms.forEach((farm:any) => merged.set(farm.id, farm));
        this.treeStateSubject.next(merged);
        const hasMore = response.data.pagination.next ? true : false;
        this.rootMetaSubject.next({
          total: response.data.pagination.total,
          hasMore,
          cursor: String(response.data.pagination.page)
        });
        console.log(farms , 'farms')
        console.log('[NavService] tree state size:', merged.size);
        this.loadingSubject.next(false);
      },
      error: () => this.loadingSubject.next(false)
    });
  }

  /**
   * Load more farms at the root level using pagination cursor
   */
  loadMoreRootFarms(): void {
    if (this.isSearchModeSubject.value) return;
    const meta = this.rootMetaSubject.value;
    if (!meta?.hasMore) return;

    this.loadingSubject.next(true);
    const currentPage = Number(meta.cursor ?? 1);
    const nextPage = currentPage + 1;
    this.getChildren({ parentType: '', parentID: '', limit: 20, cursor: nextPage }).subscribe({
      next: response => {
        const farms = response.data.data.map((farm:any) => ({
          ...farm,
          parentID: 'root',
          expanded: false,
          loading: false,
          children: [],
          hasMore: false,
          cursor: undefined
        }));
        const current = this.treeStateSubject.value;
        const merged = new Map(current);
        farms.forEach((farm:any) => merged.set(farm.id, farm));
        this.treeStateSubject.next(merged);
        const hasMore = response.data.pagination.next ? true : false;
        this.rootMetaSubject.next({
          total: response.data.pagination.total,
          hasMore,
          cursor: String(response.data.pagination.page)
        });
        this.loadingSubject.next(false);
      },
      error: () => this.loadingSubject.next(false)
    });
  }

  /**
   * Restore tree state from URL parameters
   */
  restoreFromUrl(): void {
    const url = new URL(window.location.href);
    const meterId = url.searchParams.get('meterId');
    
    if (meterId) {
      // Ensure root farms are present so the tree has starting nodes
      this.loadRootFarms();
      this.getMeterPath(meterId).subscribe();
    } else {
      this.loadRootFarms();
    }
    this.loadStats();
  }

  private expandPath(path: NavNode[]): void {
    const currentState = this.treeStateSubject.value;
    const newState = new Map(currentState);
    
    // Add all nodes in the path
    path.forEach(node => {
      if (!newState.has(node.id)) {
        newState.set(node.id, {
          ...node,
          expanded: true,
          loading: false,
          children: []
        });
      }
    });
    
    // Expand each node and load children
    path.forEach((node, index) => {
      if (index < path.length - 1) { // Don't expand the last node (meter)
        if (node.type === 'farm' || node.type === 'section' || node.type === 'point') {
          this.expandNode(node.id, node.type);
        }
      }
    });
    
    this.treeStateSubject.next(newState);
  }

  /**
   * Build and show a filtered hierarchy based on search results.
   */
  showFilteredHierarchy(items: SearchItem[]): void {
    const state = new Map<string, NavNode>();

    const ensure = (id: string, node: NavNode): NavNode => {
      const existing = state.get(id);
      if (existing) return existing;
      const created: NavNode = { ...node, children: node.children || [] };
      state.set(id, created);
      return created;
    };

    const pmLevel = (localStorage.getItem('pmLevel') || '').toString();
    const isLevel2 = pmLevel === '2';
    const isLevel3 = pmLevel === '3';
    const validItems = (Array.isArray(items) ? items : []).filter((item: any) => {
      const hasMeterPath = Boolean(item?.meter?.id && item?.point?.id);
      if (isLevel2 || isLevel3) {
        return hasMeterPath && (!isLevel2 || Boolean(item?.section?.id));
      }
      return hasMeterPath && Boolean(item?.farm?.id && item?.section?.id);
    });

    for (const item of validItems) {
      if (isLevel2) {
        // pmLevel 2: Sections are root; structure: section -> point -> meter
        const section = ensure(item.section.id, {
          id: item.section.id,
          name: item.section.name,
          type: 'section',
          parentID: 'root',
          expanded: true,
          loading: false,
          children: []
        });
        const pointData: any = (item as any).point || {};
        const thinPrepaymentStatus = pointData.thinPrepaymentStatus;
        const isThinPrepaymentEnabled = thinPrepaymentStatus === 'enabled';
        const point = ensure(item.point.id, {
          id: item.point.id,
          name: item.point.name,
          type: 'point',
          parentID: section.id,
          expanded: true,
          loading: false,
          children: [],
          thinPrepaymentStatus,
          showWallet: pointData.showWallet ?? isThinPrepaymentEnabled,
          showStatement: pointData.showStatement ?? isThinPrepaymentEnabled,
          walletReady: pointData.walletReady
        });
        const meterId = item.meter.id;
        if (!state.has(meterId)) {
          const meter: NavNode = {
            id: meterId,
            name: item.meter.name,
            type: 'meter',
            parentID: point.id,
            meterType: item.meter.meterType
          } as any;
          state.set(meterId, meter);
          point.children = point.children || [];
          point.children.push(meter);
        }
        if (!section.children!.some(c => c.id === point.id)) {
          section.children!.push(point);
        }
      } else if (isLevel3) {
        // pmLevel 3: Metering Points are root; structure: point -> meter
        const pointData: any = (item as any).point || {};
        const thinPrepaymentStatus = pointData.thinPrepaymentStatus;
        const isThinPrepaymentEnabled = thinPrepaymentStatus === 'enabled';
        const point = ensure(item.point.id, {
          id: item.point.id,
          name: item.point.name,
          type: 'point',
          parentID: 'root',
          expanded: true,
          loading: false,
          children: [],
          thinPrepaymentStatus,
          showWallet: pointData.showWallet ?? isThinPrepaymentEnabled,
          showStatement: pointData.showStatement ?? isThinPrepaymentEnabled,
          walletReady: pointData.walletReady
        });
        const meterId = item.meter.id;
        if (!state.has(meterId)) {
          const meter: NavNode = {
            id: meterId,
            name: item.meter.name,
            type: 'meter',
            parentID: point.id,
            meterType: item.meter.meterType
          } as any;
          state.set(meterId, meter);
          point.children = point.children || [];
          point.children.push(meter);
        }
      } else {
        // pmLevel 1 and others: Farms are root; structure: farm -> section -> point -> meter
        const farm = ensure(item.farm.id, {
          id: item.farm.id,
          name: item.farm.name,
          type: 'farm',
          parentID: 'root',
          expanded: true,
          loading: false,
          children: []
        });
        const section = ensure(item.section.id, {
          id: item.section.id,
          name: item.section.name,
          type: 'section',
          parentID: farm.id,
          expanded: true,
          loading: false,
          children: []
        });
        const pointData: any = (item as any).point || {};
        const thinPrepaymentStatus = pointData.thinPrepaymentStatus;
        const isThinPrepaymentEnabled = thinPrepaymentStatus === 'enabled';
        const point = ensure(item.point.id, {
          id: item.point.id,
          name: item.point.name,
          type: 'point',
          parentID: section.id,
          expanded: true,
          loading: false,
          children: [],
          thinPrepaymentStatus,
          showWallet: pointData.showWallet ?? isThinPrepaymentEnabled,
          showStatement: pointData.showStatement ?? isThinPrepaymentEnabled,
          walletReady: pointData.walletReady
        });
        const meterId = item.meter.id;
        if (!state.has(meterId)) {
          const meter: NavNode = {
            id: meterId,
            name: item.meter.name,
            type: 'meter',
            parentID: point.id,
            meterType: item.meter.meterType
          } as any;
          state.set(meterId, meter);
          point.children = point.children || [];
          point.children.push(meter);
        }
        if (!farm.children!.some(c => c.id === section.id)) {
          farm.children!.push(section);
        }
        if (!section.children!.some(c => c.id === point.id)) {
          section.children!.push(point);
        }
      }
    }

    this.treeStateSubject.next(state);
  }

  private handleError(message: string, error: any): void {
    console.error(message, error);
    this.errorSubject.next(message);
    this.loadingSubject.next(false);
  }
}
